import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Dropdown } from 'react-bootstrap';
import styled from 'styled-components';
import { StyledCustomButton } from '../styledComponents/StyledCustomButton';
import CalenderIcon from '../svgIcons/CalenderIcon';
import moment from 'moment';
import StyledButton from '../styledComponents/StyledButton';
import useOutsideClick from '../../../hooks/useOutsideClick';
import {
  DateRangePicker,
  defaultInputRanges,
  defaultStaticRanges,
} from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { isEqual, startOfDay, startOfYear } from 'date-fns';

const StyledDatePicker = styled.div`
  * {
    font-size: inherit;
    font-weight: 500;
  }
  .rdrDateDisplay,
  .rdrStartEdge,
  .rdrInRange,
  .rdrDayEndPreview,
  .rdrDayStartPreview,
  .rdrDayInPreview,
  .rdrEndEdge {
    color: ${({ theme }) => theme.primary} !important;
  }
  .rdrStaticRangeSelected,
  .rdrStaticRange:hover {
    background-color: ${({ theme }) => theme.primary} !important;
    color: ${({ theme }) => theme.white} !important;

    span {
      background-color: ${({ theme }) => theme.primary} !important;
      border-radius: 5px;
    }
  }
  .rdrStaticRange {
    border: 0 !important;
    margin-bottom: 5px !important;
    border-radius: 5px;
  }
  .rdrDefinedRangesWrapper {
    padding: 5px 10px 0 0 !important;
  }
  .rdrDayToday .rdrDayNumber span:after {
    background-color: ${({ theme }) => theme.primary} !important;
  }
  .rdrDayToday:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span:after {
    background-color: ${({ theme }) => theme.white} !important;
  }
`;

const CalenderFooter = styled.div`
  padding: 0.625rem 1rem;
  border-top: 1px solid ${({ theme }) => theme.inputBorder};
  text-align: right;
  button {
    margin-left: 10px;
    line-height: 17px;
    font-size: 14px;
    padding: 10px 20px;
    height: fit-content;
    font-weight: 500;
  }
`;

const DropdownWrapper = styled.div`
  .caret-off::after {
    display: none;
  }

  .dropdown-menu {
    padding: 0;
  }
`;

const CustomDateRangePicker = ({ dateRangeSelection, onChange, disabled }) => {

  const ref = useRef();
  const [show, setShow] = useState(false);
  const [dateSelection, setDateSelection] = useState(dateRangeSelection ?? []);

  const handleReset = () => {
    setDateSelection(dateRangeSelection);
  };

  const handleApply = () => {
    onChange(dateSelection);
    setShow(false);
  };

  const handleClose = () => {
    if (show) {
      setShow(false);
    }
  };

  useEffect(() => {
    setDateSelection(dateRangeSelection);
  }, [dateRangeSelection]);

  useOutsideClick(ref, handleClose);

  return (
    <DropdownWrapper ref={ref}>
      <Dropdown show={show} autoClose="false">
        <Dropdown.Toggle as={'div'} id="dropdown-basic" className="caret-off">
          <StyledButton
            variant="outline"
            $fontWeight="500"
            className={show ? 'active' : ''}
            disabled={disabled}
            onClick={() => setShow((prev) => !prev)}
          >
            <span>
              {dateRangeSelection.length && dateRangeSelection[0].startDate
                ? moment(dateRangeSelection[0].startDate)?.format(
                    'MMM Do, yyyy',
                  )
                : '-'}{' '}
              -{' '}
              {dateRangeSelection.length && dateRangeSelection[0].endDate
                ? moment(dateRangeSelection[0].endDate)?.format('MMM Do, yyyy')
                : '-'}
            </span>
            <span className="ms-2">
              <CalenderIcon size={25} />
            </span>
          </StyledButton>
        </Dropdown.Toggle>

        <Dropdown.Menu show={show}>
          <Container>
            <Row>
              <StyledDatePicker>
                <DateRangePicker
                  onChange={(data) => {
                    const { startDate, endDate } = data.selection;

                    // Check if both startDate and endDate are the same
                    if (
                      new Date(startDate).getTime() ===
                      new Date(endDate).getTime()
                    ) {
                      const newEndDate = new Date(startDate); // Copy the startDate

                      // Set the newEndDate to 23:59:59.999 (end of the same day)
                      newEndDate.setHours(23, 59, 59, 999);

                      // Update the date selection with the modified endDate
                      setDateSelection([
                        {
                          ...data.selection,
                          endDate: newEndDate,
                        },
                      ]);
                    } else {
                      // If startDate and endDate are different, set endDate time to 23:59:59
                      const newEndDate = new Date(endDate);
                      newEndDate.setHours(23, 59, 59, 999);

                      // Update the date selection with modified endDate
                      setDateSelection([
                        {
                          ...data.selection,
                          endDate: newEndDate,
                        },
                      ]);
                    }
                  }}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={2}
                  ranges={dateSelection}
                  direction="horizontal"
                  renderStaticRangeLabel={() => 'Year to Date'}
                  staticRanges={[
                    ...defaultStaticRanges,
                    {
                      hasCustomRendering: true,
                      range: () => ({
                        startDate: startOfYear(new Date()),
                        endDate: new Date(),
                      }),
                      isSelected() {
                        return (
                          isEqual(
                            startOfDay(new Date(dateSelection[0].startDate)),
                            startOfDay(startOfYear(new Date())),
                          ) &&
                          isEqual(
                            startOfDay(new Date(dateSelection[0].endDate)),
                            startOfDay(new Date()),
                          )
                        );
                      },
                    },
                  ]}
                  inputRanges={[defaultInputRanges[0]]}
                />
                <CalenderFooter className="border-top footerPosition">
                  <StyledCustomButton.BtnOutlineDefault
                    className="resetBtn"
                    onClick={handleReset}
                  >
                    Reset
                  </StyledCustomButton.BtnOutlineDefault>
                  <StyledCustomButton.BtnDefault
                    className="applyBtn"
                    onClick={handleApply}
                    disabled={
                      dateSelection.length &&
                      (!dateSelection[0]?.startDate ||
                        !dateSelection[0]?.endDate)
                    }
                  >
                    Apply
                  </StyledCustomButton.BtnDefault>
                </CalenderFooter>
              </StyledDatePicker>
            </Row>
          </Container>
        </Dropdown.Menu>
      </Dropdown>
    </DropdownWrapper>
  );
};

export default CustomDateRangePicker;
